import { useCallback, useContext } from 'react';
import { PresetContext } from '../contexts/PresetContext';
import { get, isObject } from 'lodash';
import { defaultFormData, oneOfPaths, requiredPaths } from '../constants/schema';

export const useSelectedObject = () => {
  const data = useContext(PresetContext);
  if (!data) {
    throw new Error('useSchemaData must be used within a SchemaProvider');
  }

  const { formData, workflow, setFormData, removeFromFormData: deleteSelectedObjects } = data;

  //Selects given path by selecting all its unselected parents first and then the given path itself
  //It does so by traversing the form data level by level to the provided path, if any of the nodes does not exist, it creates it.
  //It creates a shallow copy of each level to make sure the original object is not mutated
  const addSelectedObjects = useCallback(
    (path: string, providedFormData?: Record<string, unknown>): Record<string, unknown> => {
      const clonedFormData = { ...(providedFormData ?? workflow.config) }; //Performing shallow on first level and then on each modified level to make sure original object is not mutated
      let traversedFormData: Record<string, any> = clonedFormData;
      const pathArray = path.split('.');
      const fullPath = [];

      for (const key of pathArray) {
        fullPath.push(key);
        if (traversedFormData[key] !== undefined) {
          traversedFormData[key] = shallowCopy(traversedFormData[key]); // Performs shallow copy on each level to make sure original object is not mutated
        } else {
          traversedFormData[key] = getValue(fullPath.join('.'));
        }
        traversedFormData = traversedFormData[key];
      }
      setFormData(clonedFormData);
      return clonedFormData;
    },
    [workflow.config, setFormData]
  );

  const isObjectSelected = useCallback(
    (path: string) => {
      let traversedFormData: Record<string, any> = formData;
      const splitPath = path.split('.');
      for (let path of splitPath) {
        traversedFormData = traversedFormData[path];
        if (traversedFormData === undefined) {
          return false;
        }
      }
      return true;
    },
    [formData]
  );

  //Toggles path selection
  const updatedSelectedObjects = useCallback(
    (path: string) => {
      if (isObjectSelected(path)) {
        return deleteSelectedObjects(path);
      } else {
        addSelectedObjects(path);
      }
    },
    [addSelectedObjects, deleteSelectedObjects, isObjectSelected]
  );

  return {
    isObjectSelected,
    updatedSelectedObjects,
    addSelectedObjects,
    deleteSelectedObjects,
  };
};

const shallowCopy = (source: unknown) => {
  if (Array.isArray(source)) {
    return [...source];
  }
  if (isObject(source)) {
    return { ...source! };
  }
  return source;
};

const getDirectRequiredChildren = (path: string): string[] => {
  const pathDepth = path.split('.').length;
  return requiredPaths.filter((p) => p.startsWith(path + '.') && p.split('.').length === pathDepth + 1);
};

//Returns bare minimum data object for given path to be validly selected (or in case of "leaf nodes" - simple input paths - just a default value).
//For oneOf paths, selects first option. For group layout paths, fills in all required children by recursively calling itself
const getValue = (path: string): unknown => {
  const defaultValue = getDefaultValue(path);

  if (!isObject(defaultValue)) {
    // If not an object, just return the default value - probably a primitive input like number - basically a leaf node
    return defaultValue;
  }

  //If one of then select first option. Calls getValue recursively to make sure that after first option is selected, all its required children are also selected (and if any of them is, then to fill in their required children)
  if (oneOfPaths.includes(path)) {
    const firstChildKey = Object.keys(defaultValue)[0];
    return { [firstChildKey]: getValue(`${path}.${firstChildKey}`) };
  }

  //If group layout (object that is not oneOf in schema) fill in all required children. Calls getValue recursively to make sure that all required children of just selected children are also selected :)
  //If none of the children are required, then will just return an empty object
  const requiredChildren = getDirectRequiredChildren(path);
  const requiredChildrenValues = requiredChildren.reduce(
    (acc, childPath) => {
      acc[childPath.split('.').pop()!] = getValue(childPath);
      return acc;
    },
    {} as Record<string, any>
  );
  return requiredChildrenValues;
};

//Retrieves default value from defaultFormData for gien path
export const getDefaultValue = (path: string | string[]) => {
  const pathString = Array.isArray(path) ? path.join('.') : path;
  const pathWithZeroedIndexes = pathString.replace(/(?<=\.)\d+(?=\.|$)/g, '0'); // Replace all indexes with 0 to get default value - in defaultFormData all defaults for arrays are "at index 0"
  return get(defaultFormData, pathWithZeroedIndexes);
};
